import React, { useState } from 'react';
import { 
  Book, Home, Calendar, CreditCard, Users, Settings, BarChart3, MessageSquare,
  Building2, Globe, Shield, ChevronRight, Search, Clock, CheckCircle, AlertTriangle,
  Mail, Phone, HelpCircle, Smartphone, Link, RefreshCw, Upload, Download, Bell,
  FileText, DollarSign, UserPlus, LogIn, Key, Layers, Zap, Cloud, Image, Camera, Lock, Star, Send, LogOut, Flag
} from 'lucide-react';
import { Input } from '@/components/ui/input';

const DocsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = [
    { id: 'getting-started', label: 'Getting Started', icon: Zap },
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'properties', label: 'Properties', icon: Building2 },
    { id: 'bookings', label: 'Bookings & Availability', icon: Calendar },
    { id: 'guest-portal', label: 'Guest Portal & Payments', icon: CreditCard },
    { id: 'calendar', label: 'Calendar & iCal', icon: RefreshCw },
    { id: 'messaging', label: 'Guest Messaging', icon: MessageSquare },
    { id: 'channels', label: 'Channels & Integrations', icon: Globe },
    { id: 'cloudinary', label: 'Cloudinary & Photos', icon: Cloud },
    { id: 'payment-gateways', label: 'Payment Gateways', icon: CreditCard },
    { id: 'booking-widget', label: 'Booking Widget', icon: Link },
    { id: 'reports', label: 'Reports & Analytics', icon: BarChart3 },
    { id: 'team', label: 'Team Management', icon: Users },
    { id: 'reviews', label: 'Guest Reviews', icon: Star },
    { id: 'testimonials', label: 'Platform Testimonials', icon: MessageSquare },
    { id: 'email-templates', label: 'Email Templates', icon: Send },
    { id: 'early-checkout', label: 'Early Checkout', icon: LogOut },
    { id: 'moderation', label: 'Content Moderation', icon: Shield },
    { id: 'security', label: 'Security & Passwords', icon: Lock },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'subscription', label: 'Subscription & Billing', icon: DollarSign },
    { id: 'admin', label: 'Platform Admin', icon: Shield },
  ];

  const filteredSections = sections.filter(s => 
    s.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="docs-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center gap-3 mb-4">
            <Book className="w-8 h-8 text-pink-400" />
            <h1 className="text-2xl sm:text-3xl font-bold">Documentation & User Guide</h1>
          </div>
          <p className="text-slate-300 max-w-2xl">
            Complete guide to using iHostPMS - your all-in-one property management solution for vacation rentals.
          </p>
          
          {/* Search */}
          <div className="mt-6 max-w-md relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
              data-testid="docs-search"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <aside className="lg:w-64 flex-shrink-0">
            <nav className="sticky top-4 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              {filteredSections.map(section => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  data-testid={`docs-nav-${section.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors ${
                    activeSection === section.id
                      ? 'bg-pink-50 text-pink-600 border-l-4 border-pink-500'
                      : 'text-slate-600 hover:bg-slate-50 border-l-4 border-transparent'
                  }`}
                >
                  <section.icon className="w-4 h-4" />
                  {section.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Getting Started */}
            {activeSection === 'getting-started' && (
              <DocSection title="Getting Started" icon={Zap}>
                <DocCard title="Welcome to iHostPMS">
                  <p>iHostPMS is a comprehensive property management system designed for vacation rental hosts. 
                  Whether you manage a single property or multiple listings across various platforms, 
                  iHostPMS helps you streamline operations, prevent double bookings, and maximize revenue.</p>
                </DocCard>
                
                <DocCard title="Quick Start Guide">
                  <ol className="list-decimal list-inside space-y-3 text-slate-600">
                    <li><strong>Create Account:</strong> Sign up with email or Google OAuth</li>
                    <li><strong>Add Property:</strong> Go to Properties → Add Property and fill in details</li>
                    <li><strong>Configure Pricing:</strong> Set base price, seasonal rates, and discounts</li>
                    <li><strong>Set Availability:</strong> Block dates, set minimum stays</li>
                    <li><strong>Connect Channels:</strong> Link Airbnb, Booking.com via iCal sync</li>
                    <li><strong>Receive Bookings:</strong> Guests book and pay through secure portal</li>
                  </ol>
                </DocCard>

                <DocCard title="Key Features">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <FeatureItem icon={Building2} title="Multi-Property Management" desc="Manage unlimited properties from one dashboard" />
                    <FeatureItem icon={Calendar} title="Smart Calendar" desc="Visual availability with conflict prevention" />
                    <FeatureItem icon={CreditCard} title="Secure Payments" desc="Stripe & M-Pesa integration" />
                    <FeatureItem icon={Globe} title="Channel Sync" desc="iCal integration with OTA platforms" />
                    <FeatureItem icon={MessageSquare} title="Guest Messaging" desc="Built-in communication system" />
                    <FeatureItem icon={BarChart3} title="Analytics" desc="Revenue & occupancy insights" />
                  </div>
                </DocCard>
              </DocSection>
            )}

            {/* Dashboard */}
            {activeSection === 'dashboard' && (
              <DocSection title="Dashboard" icon={Home}>
                <DocCard title="Dashboard Overview">
                  <p>Your dashboard provides a real-time snapshot of your rental business with key metrics and recent activity.</p>
                </DocCard>

                <DocCard title="Key Metrics">
                  <ul className="space-y-3 text-slate-600">
                    <li><strong>Total Revenue:</strong> Sum of all booking payments (current month)</li>
                    <li><strong>Active Bookings:</strong> Current confirmed + checked-in bookings</li>
                    <li><strong>Occupancy Rate:</strong> Percentage of booked nights vs available</li>
                    <li><strong>Awaiting Payment:</strong> Provisional bookings awaiting guest payment</li>
                  </ul>
                </DocCard>

                <DocCard title="Recent Activity">
                  <p>Shows the latest 10 activities including:</p>
                  <ul className="list-disc list-inside text-slate-600 mt-2">
                    <li>New bookings (with status indicator)</li>
                    <li>Payment confirmations</li>
                    <li>Guest messages</li>
                    <li>Check-ins and check-outs</li>
                  </ul>
                </DocCard>
              </DocSection>
            )}

            {/* Properties */}
            {activeSection === 'properties' && (
              <DocSection title="Properties" icon={Building2}>
                <DocCard title="Property Management">
                  <p>Manage all your vacation rental properties in one place. Each property has its own profile, pricing, availability, and booking settings.</p>
                </DocCard>

                <DocCard title="Adding a Property">
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Click "Add Property" button</li>
                    <li>Enter basic info: name, type, location</li>
                    <li>Set capacity: bedrooms, bathrooms, max guests</li>
                    <li>Write descriptions for listings</li>
                    <li>Upload photos (drag & drop supported)</li>
                    <li>Configure pricing and minimum stay</li>
                    <li>Save to activate</li>
                  </ol>
                </DocCard>

                <DocCard title="Property Setup Tabs">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-slate-800">Details Tab</h4>
                      <p className="text-slate-600 text-sm">Basic info, capacity, descriptions, house rules, check-in instructions, and timing settings.</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">Amenities & Facilities</h4>
                      <p className="text-slate-600 text-sm">Select from 80+ amenities like WiFi, Pool, Kitchen, Parking. Required for OTA listings.</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">Photo Gallery</h4>
                      <p className="text-slate-600 text-sm">Upload, reorder, and manage property photos. First photo becomes the cover image.</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">Location</h4>
                      <p className="text-slate-600 text-sm">Address, coordinates, and neighborhood description for accurate mapping.</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">Rates & Pricing</h4>
                      <p className="text-slate-600 text-sm">Base price, seasonal rates, occupancy pricing, length-of-stay discounts.</p>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Auto-Cancellation Setting" highlight>
                  <p className="text-slate-600 mb-3">
                    Set how long unpaid provisional bookings remain active before automatic cancellation.
                    Found in <strong>Property Setup → Details tab</strong>.
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="font-semibold text-amber-800 mb-2">Options:</h4>
                    <ul className="text-amber-700 text-sm space-y-1">
                      <li>• 12 hours - For high-demand properties</li>
                      <li>• 24 hours - Standard setting</li>
                      <li>• 48 hours (default) - Balanced approach</li>
                      <li>• 72 hours - More flexible</li>
                      <li>• 7 days - Maximum flexibility</li>
                    </ul>
                  </div>
                </DocCard>
              </DocSection>
            )}

            {/* Bookings & Availability */}
            {activeSection === 'bookings' && (
              <DocSection title="Bookings & Availability" icon={Calendar}>
                <DocCard title="Booking Management">
                  <p>View, create, and manage all bookings across your properties. The system automatically prevents double bookings and manages waitlists.</p>
                </DocCard>

                <DocCard title="Booking Statuses" highlight>
                  <div className="space-y-3">
                    <StatusItem color="bg-amber-500" title="Provisional" desc="New booking awaiting payment. Guest receives payment link via email." />
                    <StatusItem color="bg-purple-500" title="Waitlist" desc="Dates overlap with another provisional booking. First to pay wins." />
                    <StatusItem color="bg-emerald-500" title="Confirmed" desc="Payment received. Booking is guaranteed and dates are blocked." />
                    <StatusItem color="bg-blue-500" title="Checked-in" desc="Guest has arrived and is currently staying." />
                    <StatusItem color="bg-gray-400" title="Checked-out" desc="Guest has departed. Ready for cleaning/turnover." />
                    <StatusItem color="bg-red-500" title="Cancelled" desc="Booking was cancelled by guest, host, or system (expired)." />
                  </div>
                </DocCard>

                <DocCard title="Overbooking Prevention" highlight>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-emerald-800 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> How It Works
                    </h4>
                    <ul className="text-emerald-700 text-sm mt-2 space-y-2">
                      <li>✓ If dates overlap with a <strong>CONFIRMED</strong> booking → New booking is <strong>REJECTED</strong></li>
                      <li>✓ If dates overlap with a <strong>PROVISIONAL</strong> booking → New booking added to <strong>WAITLIST</strong></li>
                      <li>✓ First guest to complete payment gets confirmed</li>
                      <li>✓ Waitlist bookings are automatically cancelled when dates are confirmed</li>
                    </ul>
                  </div>
                </DocCard>

                <DocCard title="Creating a Booking">
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Click "Add Booking" in the Bookings page</li>
                    <li>Select property from dropdown</li>
                    <li>Enter guest details (name, email, phone)</li>
                    <li>Set check-in and check-out dates</li>
                    <li>Enter number of guests and total price</li>
                    <li>Select booking channel (Direct, Airbnb, etc.)</li>
                    <li>Submit - system checks availability automatically</li>
                  </ol>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-blue-700 text-sm">
                      <strong>Note:</strong> Guest will receive an email with a secure payment link to complete the booking.
                    </p>
                  </div>
                </DocCard>

                <DocCard title="Recording Manual Payments" highlight>
                  <p className="text-slate-600 mb-3">
                    For payments received outside the system (cash, bank transfer, etc.), you can manually record the payment:
                  </p>
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Open the booking details</li>
                    <li>Click "Record Payment" button</li>
                    <li>Select payment method (Cash, Bank Transfer, M-Pesa Manual, etc.)</li>
                    <li>Enter payment reference/receipt number</li>
                    <li>Add optional notes</li>
                    <li>Confirm - booking status changes to "Confirmed"</li>
                  </ol>
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                    <p className="text-amber-700 text-sm">
                      <strong>Important:</strong> Guest receives confirmation email with property details and QR code after manual payment is recorded.
                    </p>
                  </div>
                </DocCard>
              </DocSection>
            )}

            {/* Guest Portal & Payments */}
            {activeSection === 'guest-portal' && (
              <DocSection title="Guest Portal & Payments" icon={CreditCard}>
                <DocCard title="Guest Payment Portal">
                  <p>When a booking is created, the guest receives an email with a secure link to complete their payment and view booking details.</p>
                </DocCard>

                <DocCard title="Payment Flow" highlight>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center text-pink-600 font-bold text-sm">1</div>
                      <div>
                        <h4 className="font-semibold text-slate-800">Booking Created</h4>
                        <p className="text-slate-600 text-sm">Host creates booking with guest email. System generates secure payment token.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center text-pink-600 font-bold text-sm">2</div>
                      <div>
                        <h4 className="font-semibold text-slate-800">Email Sent</h4>
                        <p className="text-slate-600 text-sm">Guest receives email with booking summary, total price, and payment deadline.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center text-pink-600 font-bold text-sm">3</div>
                      <div>
                        <h4 className="font-semibold text-slate-800">Guest Creates Account</h4>
                        <p className="text-slate-600 text-sm">Guest clicks link, creates account (name, email, password), proceeds to payment.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center text-pink-600 font-bold text-sm">4</div>
                      <div>
                        <h4 className="font-semibold text-slate-800">Payment Completed</h4>
                        <p className="text-slate-600 text-sm">Guest pays via Stripe (card) or M-Pesa. Booking confirmed immediately.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-bold text-sm">5</div>
                      <div>
                        <h4 className="font-semibold text-slate-800">Confirmation Email</h4>
                        <p className="text-slate-600 text-sm">Guest receives confirmation with property address, check-in instructions, and host contact.</p>
                      </div>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Payment Methods">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="border rounded-lg p-4">
                      <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-blue-500" /> Stripe (Card)
                      </h4>
                      <p className="text-slate-600 text-sm mt-2">Accept Visa, Mastercard, and other major cards. Secure checkout via Stripe.</p>
                    </div>
                    <div className="border rounded-lg p-4">
                      <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                        <Smartphone className="w-4 h-4 text-emerald-500" /> M-Pesa
                      </h4>
                      <p className="text-slate-600 text-sm mt-2">Mobile money payments for Kenya market. STK push sent to guest's phone.</p>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Waitlist Handling">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-purple-800 text-sm">
                      When multiple guests book the same dates with provisional status:
                    </p>
                    <ul className="text-purple-700 text-sm mt-2 space-y-1">
                      <li>• Guest portal shows "Waitlist" notice clearly</li>
                      <li>• Message: "Another booking exists for these dates. First to pay gets confirmed."</li>
                      <li>• When one guest pays, others receive cancellation email</li>
                    </ul>
                  </div>
                </DocCard>

                <DocCard title="Unified Guest Portal" highlight>
                  <p className="text-slate-600 mb-3">
                    Guests can access their booking anytime via the Guest Portal at <code className="bg-slate-100 px-2 py-0.5 rounded">/guest-portal</code>
                  </p>
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold text-slate-800">Login Method</h4>
                      <p className="text-slate-600 text-sm">Guests login using their <strong>email address</strong> + <strong>booking ID</strong> (both sent in confirmation email)</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">Portal Features</h4>
                      <ul className="text-slate-600 text-sm list-disc list-inside mt-1">
                        <li>View booking details and property information</li>
                        <li>See check-in instructions and house rules (after payment)</li>
                        <li>Contact host via email</li>
                        <li>Complete payment for provisional bookings</li>
                        <li>Cancel booking with reason</li>
                      </ul>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Confirmation Email with QR Code">
                  <p className="text-slate-600 mb-3">After payment confirmation, guests receive an email containing:</p>
                  <ul className="text-slate-600 space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5" />
                      <span><strong>QR Code:</strong> Scannable code linking directly to guest portal</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5" />
                      <span><strong>Booking ID:</strong> Reference number for portal login</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5" />
                      <span><strong>Property Details:</strong> Full address, check-in/out times</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5" />
                      <span><strong>Check-in Instructions:</strong> Access codes, directions, etc.</span>
                    </li>
                  </ul>
                </DocCard>

                <DocCard title="Booking Cancellation">
                  <p className="text-slate-600 mb-3">Guests can cancel their booking through the portal:</p>
                  <ol className="list-decimal list-inside text-slate-600 space-y-1">
                    <li>Login to Guest Portal</li>
                    <li>Click "Cancel Booking" button</li>
                    <li>Enter cancellation reason (required)</li>
                    <li>Confirm cancellation</li>
                  </ol>
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                    <p className="text-blue-700 text-sm">
                      <strong>Note:</strong> If cancelled booking was provisional, any waitlisted bookings for the same dates are automatically promoted.
                    </p>
                  </div>
                </DocCard>
              </DocSection>
            )}

            {/* Calendar & iCal */}
            {activeSection === 'calendar' && (
              <DocSection title="Calendar & iCal Sync" icon={RefreshCw}>
                <DocCard title="Calendar View">
                  <p>The calendar provides a visual overview of bookings and availability for each property.</p>
                  <div className="mt-4 space-y-2">
                    <LegendItem color="bg-emerald-500" label="Confirmed/Paid bookings" />
                    <LegendItem color="bg-amber-500" label="Provisional (awaiting payment)" />
                    <LegendItem color="bg-purple-500" label="Waitlist bookings" />
                    <LegendItem color="bg-blue-500" label="Checked-in guests" />
                    <LegendItem color="bg-slate-500" label="Checked-out bookings" />
                    <LegendItem color="bg-gray-300" label="Blocked dates" />
                    <LegendItem color="bg-white border border-gray-300" label="Available" />
                  </div>
                </DocCard>

                <DocCard title="Click-to-Book" highlight>
                  <p className="text-slate-600 mb-3">Create bookings directly from the calendar:</p>
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li><strong>Click arrival date</strong> - First click marks check-in (highlighted green)</li>
                    <li><strong>Click departure date</strong> - Second click marks check-out</li>
                    <li><strong>Booking dialog opens</strong> - Fill in guest details</li>
                    <li><strong>Submit</strong> - Booking is created with selected dates</li>
                  </ol>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-sm text-blue-700">
                      <strong>Tip:</strong> You can also click "New Booking" button and manually enter dates.
                    </p>
                  </div>
                </DocCard>

                <DocCard title="Auto Status Updates">
                  <p className="text-slate-600 mb-3">The system automatically updates booking statuses:</p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Confirmed → Checked-in:</strong> When check-in date arrives</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Checked-in → Checked-out:</strong> When checkout date passes</span>
                    </li>
                  </ul>
                  <p className="text-sm text-slate-500 mt-3">Status updates run automatically every 5 minutes.</p>
                </DocCard>

                <DocCard title="Date Validation">
                  <p className="text-slate-600 mb-3">The calendar enforces these rules:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li>Cannot book dates in the past</li>
                    <li>Cannot set "Checked-in" for future bookings</li>
                    <li>Cannot set "Checked-out" before checkout date</li>
                    <li>Overlapping bookings are prevented</li>
                  </ul>
                </DocCard>

                <DocCard title="Blocking Dates">
                  <p className="text-slate-600">Click any available date to toggle between available and blocked. Blocked dates:</p>
                  <ul className="list-disc list-inside text-slate-600 mt-2">
                    <li>Cannot receive new bookings</li>
                    <li>Show as unavailable on OTA platforms</li>
                    <li>Useful for maintenance, personal use, or seasonal closure</li>
                  </ul>
                </DocCard>

                <DocCard title="iCal Integration">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                        <Download className="w-4 h-4" /> Export iCal Feed
                      </h4>
                      <p className="text-slate-600 text-sm mt-1">
                        Generate a public iCal URL to share with Airbnb, Booking.com, VRBO. 
                        Click "Generate Feed URL" and copy the link to your OTA calendar settings.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                        <Upload className="w-4 h-4" /> Import iCal
                      </h4>
                      <p className="text-slate-600 text-sm mt-1">
                        Subscribe to external iCal feeds from OTAs. Add the URL and iHostPMS will automatically 
                        sync bookings every 15 minutes.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                        <RefreshCw className="w-4 h-4" /> Auto-Sync
                      </h4>
                      <p className="text-slate-600 text-sm mt-1">
                        Subscribed feeds are automatically polled every 15 minutes to import new bookings 
                        and update availability across all platforms.
                      </p>
                    </div>
                  </div>
                </DocCard>
              </DocSection>
            )}

            {/* Messaging */}
            {activeSection === 'messaging' && (
              <DocSection title="Guest Messaging" icon={MessageSquare}>
                <DocCard title="Communication Hub">
                  <p>Send and receive messages with guests directly through iHostPMS. Each booking has its own conversation thread.</p>
                </DocCard>

                <DocCard title="Message Templates">
                  <p className="text-slate-600 mb-3">Create reusable templates for common messages:</p>
                  <ul className="space-y-2 text-slate-600">
                    <li><strong>Booking Confirmation:</strong> Sent automatically when payment is received</li>
                    <li><strong>Check-in Instructions:</strong> Sent 1 day before arrival (configurable)</li>
                    <li><strong>Welcome Message:</strong> Custom greeting for guests</li>
                    <li><strong>Check-out Reminder:</strong> Departure instructions</li>
                  </ul>
                </DocCard>

                <DocCard title="Template Variables">
                  <p className="text-slate-600 mb-2">Use these placeholders in templates:</p>
                  <div className="bg-slate-100 rounded-lg p-3 font-mono text-sm text-slate-700">
                    {`{{guest_name}}, {{property_name}}, {{check_in}}, {{check_out}}, 
{{num_guests}}, {{total_price}}, {{check_in_time}}, {{check_out_time}}, 
{{address}}, {{house_rules}}, {{guest_portal_link}}`}
                  </div>
                </DocCard>

                <DocCard title="Guest Portal Access">
                  <p className="text-slate-600">
                    Each booking includes a unique guest portal link where guests can:
                  </p>
                  <ul className="list-disc list-inside text-slate-600 mt-2">
                    <li>View booking details and property information</li>
                    <li>Send messages to the host</li>
                    <li>Access check-in instructions</li>
                    <li>View house rules</li>
                  </ul>
                </DocCard>
              </DocSection>
            )}

            {/* Channels */}
            {activeSection === 'channels' && (
              <DocSection title="Channels & Integrations" icon={Globe}>
                <DocCard title="Channel Manager">
                  <p>Connect your properties to multiple booking platforms (OTAs) to maximize visibility and bookings.</p>
                </DocCard>

                <DocCard title="Supported Channels">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <ChannelBadge name="Direct" color="bg-gray-100" />
                    <ChannelBadge name="Airbnb" color="bg-pink-100 text-pink-700" />
                    <ChannelBadge name="Booking.com" color="bg-blue-100 text-blue-700" />
                    <ChannelBadge name="VRBO" color="bg-indigo-100 text-indigo-700" />
                    <ChannelBadge name="Expedia" color="bg-yellow-100 text-yellow-700" />
                    <ChannelBadge name="Agoda" color="bg-purple-100 text-purple-700" />
                  </div>
                </DocCard>

                <DocCard title="iCal Sync (Current)">
                  <p className="text-slate-600">
                    Currently, channel synchronization works via iCal feeds. Export your iHostPMS calendar 
                    to OTAs and import their calendars to prevent double bookings.
                  </p>
                </DocCard>

                <DocCard title="Coming Soon: Direct API Integration">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800 text-sm">
                      Direct API connections with Airbnb, Booking.com, and Expedia are planned for future releases. 
                      This will enable real-time sync, instant booking notifications, and automated pricing updates.
                    </p>
                  </div>
                </DocCard>
              </DocSection>
            )}

            {/* Cloudinary & Photos */}
            {activeSection === 'cloudinary' && (
              <DocSection title="Cloudinary & Photos" icon={Cloud}>
                <DocCard title="Why Cloudinary?">
                  <p className="text-slate-600 mb-4">
                    Cloudinary is an industry-leading cloud-based image and video management platform that provides 
                    powerful features for hosting, transforming, and optimizing your property photos. Here's why we 
                    recommend it for iHostPMS:
                  </p>
                  <div className="space-y-3">
                    <FeatureItem 
                      icon={Zap} 
                      title="Performance & Speed" 
                      desc="Global CDN delivers images lightning-fast to guests worldwide, improving page load times and booking conversions"
                    />
                    <FeatureItem 
                      icon={Image} 
                      title="Automatic Optimization" 
                      desc="Images are automatically optimized for web - reduced file sizes without quality loss, responsive formats (WebP, AVIF)"
                    />
                    <FeatureItem 
                      icon={Camera} 
                      title="On-the-fly Transformations" 
                      desc="Resize, crop, add watermarks, adjust quality - all through URL parameters without storing multiple versions"
                    />
                    <FeatureItem 
                      icon={Shield} 
                      title="Reliable & Secure" 
                      desc="99.99% uptime SLA, secure uploads, backup redundancy - your photos are always available"
                    />
                    <FeatureItem 
                      icon={DollarSign} 
                      title="Cost Effective" 
                      desc="Free tier covers most small hosts. Pay only for what you use with transparent pricing"
                    />
                  </div>
                </DocCard>

                <DocCard title="How It Works in iHostPMS">
                  <p className="text-slate-600 mb-4">
                    Each host connects their own Cloudinary account to iHostPMS. When you upload property photos, 
                    they're stored in YOUR Cloudinary account - you maintain full ownership and control.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0 text-pink-600 font-bold text-xs">1</div>
                      <div>
                        <p className="font-medium text-slate-800">Create Cloudinary Account</p>
                        <p className="text-sm text-slate-600">Sign up free at <a href="https://cloudinary.com" target="_blank" rel="noopener noreferrer" className="text-pink-600 underline">cloudinary.com</a></p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0 text-pink-600 font-bold text-xs">2</div>
                      <div>
                        <p className="font-medium text-slate-800">Get API Credentials</p>
                        <p className="text-sm text-slate-600">From Cloudinary Dashboard → Settings → Access Keys</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0 text-pink-600 font-bold text-xs">3</div>
                      <div>
                        <p className="font-medium text-slate-800">Add to iHostPMS Settings</p>
                        <p className="text-sm text-slate-600">Go to Settings → Cloudinary section → Enter Cloud Name, API Key, API Secret</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0 text-pink-600 font-bold text-xs">4</div>
                      <div>
                        <p className="font-medium text-slate-800">Upload Photos</p>
                        <p className="text-sm text-slate-600">In Property Setup → Photos tab, click Upload and select images</p>
                      </div>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Finding Your Cloudinary Credentials">
                  <div className="space-y-4">
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Step 1: Access Your Dashboard</p>
                      <p className="text-slate-600 text-sm">Log into <a href="https://console.cloudinary.com" target="_blank" rel="noopener noreferrer" className="text-pink-600 underline">console.cloudinary.com</a></p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Step 2: Navigate to Settings</p>
                      <p className="text-slate-600 text-sm">Click the gear icon (⚙️) in the bottom-left sidebar → Select "Access Keys"</p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Step 3: Copy Your Credentials</p>
                      <ul className="text-slate-600 text-sm space-y-1">
                        <li><strong>Cloud Name:</strong> Shown at the top of your dashboard (e.g., "dxxxxxx")</li>
                        <li><strong>API Key:</strong> Listed under Access Keys</li>
                        <li><strong>API Secret:</strong> Click "Show" to reveal, then copy</li>
                      </ul>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-amber-800 text-sm">
                        <strong>Security Note:</strong> Keep your API Secret confidential. Never share it publicly or commit it to code repositories.
                      </p>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Cloudinary Free Tier">
                  <p className="text-slate-600 mb-3">Cloudinary offers a generous free tier perfect for getting started:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li><strong>25 Credits/month</strong> (~25GB storage or 25GB bandwidth)</li>
                    <li><strong>Transformations included</strong> - resize, crop, optimize</li>
                    <li><strong>Global CDN</strong> - fast delivery worldwide</li>
                    <li><strong>Unlimited properties</strong> - use across all your listings</li>
                  </ul>
                  <p className="text-slate-500 text-sm mt-3">
                    Most hosts with under 50 properties stay comfortably within the free tier.
                  </p>
                </DocCard>

                <DocCard title="Property Logos & Branding">
                  <p className="text-slate-600 mb-4">
                    Upload a custom logo for each property to enhance your brand presence in guest communications.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0 text-pink-600 font-bold text-xs">1</div>
                      <div>
                        <p className="font-medium text-slate-800">Navigate to Property Setup</p>
                        <p className="text-sm text-slate-600">Go to Properties → Select a property → Setup tab</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0 text-pink-600 font-bold text-xs">2</div>
                      <div>
                        <p className="font-medium text-slate-800">Find Logo Section</p>
                        <p className="text-sm text-slate-600">Scroll to the "Property Logo" section</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0 text-pink-600 font-bold text-xs">3</div>
                      <div>
                        <p className="font-medium text-slate-800">Upload Logo Image</p>
                        <p className="text-sm text-slate-600">Click "Select File" or paste an image URL</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0 text-pink-600 font-bold text-xs">4</div>
                      <div>
                        <p className="font-medium text-slate-800">Save Changes</p>
                        <p className="text-sm text-slate-600">Click Save and your logo will appear in guest emails and booking confirmations</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-sm text-blue-700">
                      <strong>Tip:</strong> Use a square or horizontal logo (min 200x200px) for best results. 
                      PNG with transparent background works great!
                    </p>
                  </div>
                </DocCard>

                <DocCard title="Photo Best Practices">
                  <div className="space-y-3 text-slate-600">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <p><strong>High Resolution:</strong> Upload photos at 2000x1500px or higher for best quality</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <p><strong>Landscape Orientation:</strong> Horizontal photos display better on listings</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <p><strong>Good Lighting:</strong> Natural daylight produces the most appealing images</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <p><strong>Cover Photo First:</strong> Your first uploaded photo becomes the listing thumbnail</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <p><strong>10-20 Photos:</strong> Include bedroom, bathroom, kitchen, living areas, exterior, and amenities</p>
                    </div>
                  </div>
                </DocCard>
              </DocSection>
            )}

            {/* M-Pesa Payments */}
            {activeSection === 'mpesa' && (
              <DocSection title="M-Pesa Payments" icon={Smartphone}>
                <DocCard title="M-Pesa for Booking Payments">
                  <p className="text-slate-600 mb-4">
                    M-Pesa is Kenya's most popular mobile payment system. iHostPMS integrates M-Pesa STK Push 
                    to allow guests to pay for bookings directly from their phones - no app download required.
                  </p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 text-sm">
                      <strong>How it works:</strong> When a guest clicks "Pay with M-Pesa", they receive an 
                      STK (SIM Toolkit) push notification on their phone prompting them to enter their M-Pesa PIN. 
                      Once confirmed, the payment is processed instantly.
                    </p>
                  </div>
                </DocCard>

                <DocCard title="Setting Up M-Pesa">
                  <div className="space-y-4">
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Step 1: Get Daraja API Credentials</p>
                      <p className="text-slate-600 text-sm">
                        Register at <a href="https://developer.safaricom.co.ke" target="_blank" rel="noopener noreferrer" className="text-pink-600 underline">Safaricom Daraja Portal</a> 
                        and create an app to get your Consumer Key and Consumer Secret.
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Step 2: Get Your Paybill/Till Details</p>
                      <ul className="text-slate-600 text-sm space-y-1">
                        <li><strong>Shortcode:</strong> Your M-Pesa Paybill number or Till number</li>
                        <li><strong>Passkey:</strong> Provided by Safaricom for STK Push authentication</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Step 3: Configure in iHostPMS</p>
                      <p className="text-slate-600 text-sm">
                        Go to Settings → M-Pesa Payments → Enter your credentials and enable the integration.
                      </p>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="M-Pesa Configuration Fields">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 pr-4 font-medium text-slate-800">Field</th>
                          <th className="text-left py-2 font-medium text-slate-800">Description</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-600">
                        <tr className="border-b">
                          <td className="py-2 pr-4 font-mono text-xs">Consumer Key</td>
                          <td className="py-2">API key from Daraja portal</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 pr-4 font-mono text-xs">Consumer Secret</td>
                          <td className="py-2">API secret from Daraja portal</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 pr-4 font-mono text-xs">Shortcode</td>
                          <td className="py-2">Your Paybill (6 digits) or Till number</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 pr-4 font-mono text-xs">Passkey</td>
                          <td className="py-2">STK Push passkey from Safaricom</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 pr-4 font-mono text-xs">Environment</td>
                          <td className="py-2">Sandbox (testing) or Production (live)</td>
                        </tr>
                        <tr>
                          <td className="py-2 pr-4 font-mono text-xs">Callback URL</td>
                          <td className="py-2">Optional custom URL for payment confirmations</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </DocCard>

                <DocCard title="Guest Payment Experience">
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 text-green-600 font-bold text-xs">1</div>
                      <p className="text-slate-600">Guest receives booking confirmation email with payment link</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 text-green-600 font-bold text-xs">2</div>
                      <p className="text-slate-600">Guest clicks "Pay Now" and confirms their M-Pesa number</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 text-green-600 font-bold text-xs">3</div>
                      <p className="text-slate-600">STK Push appears on guest's phone with payment amount</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 text-green-600 font-bold text-xs">4</div>
                      <p className="text-slate-600">Guest enters M-Pesa PIN to confirm payment</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 text-green-600 font-bold text-xs">5</div>
                      <p className="text-slate-600">Booking automatically confirmed, both parties notified</p>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Sandbox vs Production">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="border rounded-lg p-4">
                      <h4 className="font-semibold text-slate-800 mb-2">Sandbox (Testing)</h4>
                      <ul className="text-slate-600 text-sm space-y-1">
                        <li>• Use test credentials from Daraja</li>
                        <li>• No real money transferred</li>
                        <li>• Test with Safaricom test numbers</li>
                        <li>• Perfect for development</li>
                      </ul>
                    </div>
                    <div className="border border-green-200 rounded-lg p-4 bg-green-50/50">
                      <h4 className="font-semibold text-slate-800 mb-2">Production (Live)</h4>
                      <ul className="text-slate-600 text-sm space-y-1">
                        <li>• Use production credentials</li>
                        <li>• Real payments processed</li>
                        <li>• Funds deposited to your Paybill</li>
                        <li>• Ready for real guests</li>
                      </ul>
                    </div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                    <p className="text-amber-800 text-sm">
                      <strong>Tip:</strong> Always test thoroughly in Sandbox before switching to Production. 
                      Use the "Test Connection" button to verify your credentials.
                    </p>
                  </div>
                </DocCard>

                <DocCard title="Fallback: Manual Payment">
                  <p className="text-slate-600 mb-3">
                    If STK Push fails (network issues, phone off), guests see manual payment instructions:
                  </p>
                  <div className="bg-slate-100 rounded-lg p-4 font-mono text-sm">
                    <p>1. Go to M-Pesa → Lipa na M-Pesa → Pay Bill</p>
                    <p>2. Enter Business No: <strong>[Your Paybill]</strong></p>
                    <p>3. Enter Account No: <strong>[Booking ID]</strong></p>
                    <p>4. Enter Amount: <strong>KES [Amount]</strong></p>
                  </div>
                </DocCard>
              </DocSection>
            )}

            {/* Payment Gateways - Comprehensive */}
            {activeSection === 'payment-gateways' && (
              <DocSection title="Payment Gateways" icon={CreditCard}>
                <DocCard title="Overview">
                  <p className="text-slate-600 mb-4">
                    iHostPMS supports multiple payment gateways to accept payments from guests worldwide. 
                    You can configure payment options at three levels:
                  </p>
                  <ul className="space-y-2 text-slate-600">
                    <li><strong>Platform Level:</strong> Default gateways for subscription payments (Admin only)</li>
                    <li><strong>Host Level:</strong> Your default payment settings for all properties</li>
                    <li><strong>Property Level:</strong> Override host settings for specific properties</li>
                  </ul>
                </DocCard>

                <DocCard title="Supported Payment Gateways">
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CreditCard className="w-5 h-5 text-purple-600" />
                        <h4 className="font-semibold text-slate-800">Stripe</h4>
                      </div>
                      <ul className="text-slate-600 text-sm space-y-1">
                        <li>• Credit/Debit cards</li>
                        <li>• 135+ currencies</li>
                        <li>• Global coverage</li>
                        <li>• Instant confirmation</li>
                      </ul>
                    </div>
                    <div className="border border-green-200 rounded-lg p-4 bg-green-50/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Smartphone className="w-5 h-5 text-green-600" />
                        <h4 className="font-semibold text-slate-800">M-Pesa</h4>
                      </div>
                      <ul className="text-slate-600 text-sm space-y-1">
                        <li>• Mobile money (Kenya)</li>
                        <li>• STK Push integration</li>
                        <li>• No app required</li>
                        <li>• Instant settlement</li>
                      </ul>
                    </div>
                    <div className="border border-orange-200 rounded-lg p-4 bg-orange-50/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Globe className="w-5 h-5 text-orange-600" />
                        <h4 className="font-semibold text-slate-800">Pesapal</h4>
                      </div>
                      <ul className="text-slate-600 text-sm space-y-1">
                        <li>• Multi-channel (Africa)</li>
                        <li>• Cards, M-Pesa, Bank</li>
                        <li>• Multiple countries</li>
                        <li>• Unified checkout</li>
                      </ul>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Stripe Setup">
                  <div className="space-y-4">
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Step 1: Create Stripe Account</p>
                      <p className="text-slate-600 text-sm">
                        Sign up at <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="text-pink-600 underline">stripe.com</a> 
                        and complete business verification.
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Step 2: Get API Keys</p>
                      <p className="text-slate-600 text-sm">
                        Dashboard → Developers → API Keys → Copy Publishable Key and Secret Key
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Step 3: Configure in iHostPMS</p>
                      <p className="text-slate-600 text-sm">
                        Property Setup → Payment Gateways → Enable Stripe → Enter keys
                      </p>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="M-Pesa Setup (Kenya)">
                  <div className="space-y-4">
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Step 1: Register on Daraja</p>
                      <p className="text-slate-600 text-sm">
                        Create account at <a href="https://developer.safaricom.co.ke" target="_blank" rel="noopener noreferrer" className="text-pink-600 underline">Safaricom Daraja Portal</a>
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Step 2: Create App & Get Credentials</p>
                      <ul className="text-slate-600 text-sm space-y-1">
                        <li>• Consumer Key & Consumer Secret</li>
                        <li>• Shortcode (Paybill/Till number)</li>
                        <li>• Passkey (for STK Push)</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Step 3: Configure in Settings</p>
                      <p className="text-slate-600 text-sm">
                        Settings → M-Pesa Payments → Enter credentials → Test Connection
                      </p>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Pesapal Setup (Africa-wide)">
                  <div className="space-y-4">
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Step 1: Create Pesapal Account</p>
                      <p className="text-slate-600 text-sm">
                        Register at <a href="https://www.pesapal.com" target="_blank" rel="noopener noreferrer" className="text-pink-600 underline">pesapal.com</a>
                        and complete merchant verification.
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Step 2: Get API Credentials</p>
                      <p className="text-slate-600 text-sm">
                        Pesapal Dashboard → API → Copy Consumer Key and Consumer Secret
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Step 3: Configure in iHostPMS</p>
                      <p className="text-slate-600 text-sm">
                        Settings → Pesapal Payments → Enter credentials → Register IPN → Test Connection
                      </p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-blue-800 text-sm">
                        <strong>IPN Registration:</strong> Click "Register IPN" to set up the callback URL. 
                        This allows Pesapal to notify iHostPMS when payments are completed.
                      </p>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Per-Property Payment Settings">
                  <p className="text-slate-600 mb-4">
                    Override your default payment settings for specific properties:
                  </p>
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Go to <strong>Properties → Select Property → Setup</strong></li>
                    <li>Click the <strong>Payment Gateways</strong> tab</li>
                    <li>Toggle off <strong>"Use Host-Level Settings"</strong></li>
                    <li>Configure Stripe, M-Pesa, and/or Pesapal individually</li>
                    <li>Set a <strong>Preferred Gateway</strong> for the booking widget</li>
                    <li>Click <strong>Save Payment Settings</strong></li>
                  </ol>
                </DocCard>
              </DocSection>
            )}

            {/* Booking Widget */}
            {activeSection === 'booking-widget' && (
              <DocSection title="Booking Widget" icon={Link}>
                <DocCard title="What is the Booking Widget?">
                  <p className="text-slate-600 mb-4">
                    The iHostPMS Booking Widget allows you to embed a booking calendar directly on your own website. 
                    Guests can check availability, select dates, and make bookings without leaving your site.
                  </p>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div className="bg-pink-50 rounded-lg p-3 text-center">
                      <Calendar className="w-6 h-6 text-pink-600 mx-auto mb-2" />
                      <p className="text-sm font-medium text-slate-800">Calendar Only</p>
                      <p className="text-xs text-slate-500">Show availability</p>
                    </div>
                    <div className="bg-pink-50 rounded-lg p-3 text-center">
                      <CreditCard className="w-6 h-6 text-pink-600 mx-auto mb-2" />
                      <p className="text-sm font-medium text-slate-800">Full Booking</p>
                      <p className="text-xs text-slate-500">Complete flow</p>
                    </div>
                    <div className="bg-pink-50 rounded-lg p-3 text-center">
                      <Link className="w-6 h-6 text-pink-600 mx-auto mb-2" />
                      <p className="text-sm font-medium text-slate-800">Book Button</p>
                      <p className="text-xs text-slate-500">Link to portal</p>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Setting Up Your Widget">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0 text-pink-600 font-bold text-xs">1</div>
                      <div>
                        <p className="font-medium text-slate-800">Navigate to Widget Setup</p>
                        <p className="text-sm text-slate-600">Properties → Select Property → Setup → Booking Widget tab</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0 text-pink-600 font-bold text-xs">2</div>
                      <div>
                        <p className="font-medium text-slate-800">Choose Widget Type</p>
                        <p className="text-sm text-slate-600">Calendar Only, Full Booking Flow, or Simple Book Button</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0 text-pink-600 font-bold text-xs">3</div>
                      <div>
                        <p className="font-medium text-slate-800">Customize Design</p>
                        <p className="text-sm text-slate-600">Theme (light/dark), primary color, button text, border radius</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0 text-pink-600 font-bold text-xs">4</div>
                      <div>
                        <p className="font-medium text-slate-800">Configure Settings</p>
                        <p className="text-sm text-slate-600">Payment requirements, guest accounts, min/max stay</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0 text-pink-600 font-bold text-xs">5</div>
                      <div>
                        <p className="font-medium text-slate-800">Copy Embed Code</p>
                        <p className="text-sm text-slate-600">Choose iframe, JavaScript SDK, or direct URL</p>
                      </div>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Embed Options">
                  <div className="space-y-4">
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Option 1: Iframe Embed</p>
                      <div className="bg-slate-100 rounded-lg p-3 font-mono text-xs overflow-x-auto">
                        {`<iframe src="https://ihostpms.com/embed/[widget-key]" width="100%" height="600" frameborder="0"></iframe>`}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Simplest option - works on any website</p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Option 2: JavaScript SDK</p>
                      <div className="bg-slate-100 rounded-lg p-3 font-mono text-xs overflow-x-auto">
                        {`<div id="ihostpms-widget"></div>
<script src="https://ihostpms.com/widget.js"></script>
<script>iHostPMS.init({ key: '[widget-key]' });</script>`}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">More control, better integration</p>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 mb-2">Option 3: Direct Link</p>
                      <div className="bg-slate-100 rounded-lg p-3 font-mono text-xs">
                        https://ihostpms.com/book/[widget-key]
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Share as button or link</p>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Widget Settings Explained">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 pr-4 font-medium text-slate-800">Setting</th>
                          <th className="text-left py-2 font-medium text-slate-800">Description</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-600">
                        <tr className="border-b">
                          <td className="py-2 pr-4">Payment Required</td>
                          <td className="py-2">Required / Optional / Disabled - control payment flow</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 pr-4">Guest Account</td>
                          <td className="py-2">Required / Optional - for returning guests</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 pr-4">Min Stay</td>
                          <td className="py-2">Minimum nights required for booking</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 pr-4">Max Stay</td>
                          <td className="py-2">Maximum nights allowed per booking</td>
                        </tr>
                        <tr>
                          <td className="py-2 pr-4">Allowed Domains</td>
                          <td className="py-2">Restrict which websites can embed (security)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </DocCard>

                <DocCard title="Property Logo on Widget">
                  <p className="text-slate-600 mb-3">
                    Display your property logo on the booking widget for professional branding:
                  </p>
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Go to Property Setup → <strong>Logo & Branding</strong> tab</li>
                    <li>Upload logo via URL or Cloudinary</li>
                    <li>Logo appears on widget, emails, and guest portal</li>
                  </ol>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                    <p className="text-blue-800 text-sm">
                      <strong>Recommended:</strong> Use a square logo (200x200px+) with transparent background (PNG)
                    </p>
                  </div>
                </DocCard>
              </DocSection>
            )}

            {/* Reports */}
            {activeSection === 'reports' && (
              <DocSection title="Reports & Analytics" icon={BarChart3}>
                <DocCard title="Analytics Dashboard">
                  <p>Track your rental business performance with comprehensive reports and visualizations.</p>
                </DocCard>

                <DocCard title="Key Performance Indicators">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <KPIItem title="Total Revenue" desc="Sum of all confirmed booking payments" />
                    <KPIItem title="Occupancy Rate" desc="Percentage of booked nights" />
                    <KPIItem title="Average Daily Rate" desc="Average revenue per booked night" />
                    <KPIItem title="RevPAR" desc="Revenue per available room/night" />
                    <KPIItem title="Total Bookings" desc="Count of all bookings" />
                    <KPIItem title="Avg Length of Stay" desc="Average nights per booking" />
                    <KPIItem title="Cancellation Rate" desc="Percentage of cancelled bookings" />
                    <KPIItem title="Lead Time" desc="Average days between booking and check-in" />
                  </div>
                </DocCard>

                <DocCard title="Charts & Visualizations">
                  <ul className="space-y-2 text-slate-600">
                    <li><strong>Revenue Trends:</strong> Monthly revenue over time</li>
                    <li><strong>Booking Trends:</strong> Number of bookings by month</li>
                    <li><strong>Bookings by Channel:</strong> Distribution across OTAs</li>
                    <li><strong>Bookings by Status:</strong> Current status breakdown</li>
                    <li><strong>Top Properties:</strong> Revenue leaders</li>
                  </ul>
                </DocCard>
              </DocSection>
            )}

            {/* Settings */}
            {/* Team Management */}
            {activeSection === 'team' && (
              <DocSection title="Team Management" icon={Users}>
                <DocCard title="Overview">
                  <p>Manage your team members and their access to your properties. Invite staff, 
                  set their roles, and control what they can see and do in the system.</p>
                </DocCard>

                <DocCard title="Accessing Team Management">
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Navigate to <strong>Team Members</strong> in the sidebar</li>
                    <li>View all current team members with their roles and status</li>
                    <li>Use the <strong>Invite Member</strong> button to add new team members</li>
                  </ol>
                </DocCard>

                <DocCard title="Team Roles">
                  <div className="space-y-4 mt-2">
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                      <p className="font-semibold text-amber-800">Owner</p>
                      <p className="text-sm text-slate-600 mt-1">Full access. Can manage billing, team members, and all settings. There can only be one owner per account.</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <p className="font-semibold text-blue-800">Admin</p>
                      <p className="text-sm text-slate-600 mt-1">Can manage properties, bookings, and team members. Cannot change billing or transfer ownership.</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="font-semibold text-slate-800">Member</p>
                      <p className="text-sm text-slate-600 mt-1">Basic access to view and manage assigned properties and bookings.</p>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Inviting Team Members">
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Click the <strong>Invite Member</strong> button</li>
                    <li>Enter the team member's name and email address</li>
                    <li>Select their role (Admin or Member)</li>
                    <li>Click <strong>Send Invitation</strong></li>
                    <li>An email with temporary login credentials will be sent automatically</li>
                    <li>The new user must change their password on first login</li>
                  </ol>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-sm text-blue-700"><strong>Note:</strong> The temporary password is auto-generated for security. Users will be prompted to set their own password when they first log in.</p>
                  </div>
                </DocCard>

                <DocCard title="Managing Team Members">
                  <ul className="list-disc list-inside text-slate-600 space-y-2">
                    <li><strong>Change Role:</strong> Owners can change a member's role from Admin to Member or vice versa</li>
                    <li><strong>Enable/Disable:</strong> Temporarily disable a team member's access without removing them</li>
                    <li><strong>Remove:</strong> Permanently remove a team member from your account</li>
                  </ul>
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="text-sm text-amber-700"><strong>Permissions:</strong> Only owners can change roles. Admins can disable members but cannot modify other admins.</p>
                  </div>
                </DocCard>
              </DocSection>
            )}

            {/* Guest Reviews */}
            {activeSection === 'reviews' && (
              <DocSection title="Guest Reviews" icon={Star}>
                <DocCard title="Overview">
                  <p>Collect, manage, and display guest reviews to build trust and attract more bookings. 
                  Reviews can be requested automatically after checkout or manually from the bookings page.</p>
                </DocCard>

                <DocCard title="Enabling Guest Reviews">
                  <p className="text-slate-600 mb-4">To enable guest reviews for your properties:</p>
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Go to <strong>Settings → Guest Reviews</strong></li>
                    <li>Toggle <strong>"Enable Reviews"</strong> to on</li>
                    <li>Configure auto-request settings if desired</li>
                    <li>Save your settings</li>
                  </ol>
                </DocCard>

                <DocCard title="Review Settings" highlight>
                  <div className="space-y-3 text-slate-600">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <p><strong>Auto-request:</strong> Automatically send review request emails after guest checkout</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <p><strong>Days after checkout:</strong> Choose when to send the request (1-7 days)</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <p><strong>Auto-publish:</strong> Publish reviews automatically or require manual approval</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <p><strong>OTA Import:</strong> Import reviews from Airbnb, Booking.com, etc.</p>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Requesting Reviews Manually">
                  <p className="text-slate-600 mb-4">For completed bookings (checked-out status):</p>
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Go to <strong>Bookings</strong> page</li>
                    <li>Find a checked-out booking with a guest email</li>
                    <li>Click the <strong>"Request Review"</strong> button</li>
                    <li>Guest receives an email with a secure link to submit their review</li>
                  </ol>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-sm text-blue-700"><strong>Note:</strong> The review link expires after 30 days for security.</p>
                  </div>
                </DocCard>

                <DocCard title="Managing Reviews">
                  <p className="text-slate-600 mb-4">Access the Reviews page from the sidebar to:</p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>View all reviews with stats (average rating, total, pending count)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>Filter by property or status (pending, published, rejected)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>Approve or reject pending reviews</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>Respond publicly to guest reviews</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>Import reviews from OTA platforms</span>
                    </li>
                  </ul>
                </DocCard>

                <DocCard title="Where Reviews Are Displayed">
                  <p className="text-slate-600 mb-4">Approved reviews appear in:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li>Property detail pages on the marketplace</li>
                    <li>Embeddable booking widget (if enabled)</li>
                    <li>Property cards showing average rating</li>
                  </ul>
                </DocCard>

                <DocCard title="Importing OTA Reviews">
                  <p className="text-slate-600 mb-4">To import reviews from Airbnb, Booking.com, etc.:</p>
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Go to <strong>Reviews</strong> page</li>
                    <li>Click <strong>"Import Review"</strong> button</li>
                    <li>Select the property and source platform</li>
                    <li>Enter the review details (rating, content, guest name, date)</li>
                    <li>Click Import - the review is automatically approved</li>
                  </ol>
                </DocCard>
              </DocSection>
            )}

            {/* Email Templates */}
            {activeSection === 'email-templates' && (
              <DocSection title="Email Templates" icon={Send}>
                <DocCard title="Overview">
                  <p>Customize the automated emails sent to your guests. Personalize your communication 
                  with your brand voice while keeping all the essential information.</p>
                </DocCard>

                <DocCard title="Available Templates">
                  <div className="space-y-4">
                    <div className="border rounded-lg p-4">
                      <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                        <Star className="w-4 h-4 text-yellow-500" /> Review Request Email
                      </h4>
                      <p className="text-slate-600 text-sm mt-2">
                        Sent to guests after checkout to request a review of their stay.
                      </p>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Template Variables" highlight>
                  <p className="text-slate-600 mb-4">Use these placeholders in your templates - they'll be replaced with actual values:</p>
                  <div className="bg-slate-100 rounded-lg p-4">
                    <table className="w-full text-sm">
                      <tbody className="text-slate-600">
                        <tr className="border-b border-slate-200">
                          <td className="py-2 pr-4 font-mono text-xs text-pink-600">{'{guest_name}'}</td>
                          <td className="py-2">Guest's full name</td>
                        </tr>
                        <tr className="border-b border-slate-200">
                          <td className="py-2 pr-4 font-mono text-xs text-pink-600">{'{property_name}'}</td>
                          <td className="py-2">Property name</td>
                        </tr>
                        <tr className="border-b border-slate-200">
                          <td className="py-2 pr-4 font-mono text-xs text-pink-600">{'{review_link}'}</td>
                          <td className="py-2">Review submission button/link</td>
                        </tr>
                        <tr className="border-b border-slate-200">
                          <td className="py-2 pr-4 font-mono text-xs text-pink-600">{'{check_in}'}</td>
                          <td className="py-2">Check-in date</td>
                        </tr>
                        <tr>
                          <td className="py-2 pr-4 font-mono text-xs text-pink-600">{'{check_out}'}</td>
                          <td className="py-2">Check-out date</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </DocCard>

                <DocCard title="Customizing Templates">
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Go to <strong>Settings → Email Templates</strong></li>
                    <li>Find the template you want to customize</li>
                    <li>Edit the <strong>Subject Line</strong> with variables</li>
                    <li>Edit the <strong>Email Body</strong> (HTML supported)</li>
                    <li>Save your settings</li>
                  </ol>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-sm text-blue-700">
                      <strong>Tip:</strong> Leave fields empty to use the default templates. 
                      Your custom templates maintain the same professional styling.
                    </p>
                  </div>
                </DocCard>

                <DocCard title="Example: Review Request Template">
                  <p className="text-slate-600 mb-3">Here's an example custom template:</p>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <p className="font-semibold text-slate-700 mb-2">Subject:</p>
                    <p className="text-sm text-slate-600 font-mono mb-4">We'd love your feedback on {'{property_name}'}, {'{guest_name}'}!</p>
                    <p className="font-semibold text-slate-700 mb-2">Body:</p>
                    <pre className="text-sm text-slate-600 whitespace-pre-wrap font-mono">{`Dear {guest_name},

Thank you for choosing {property_name} for your recent stay ({check_in} - {check_out}).

We hope you had an amazing experience! Your feedback helps us improve and helps other travelers make informed decisions.

{review_link}

Warm regards,
The Team`}</pre>
                  </div>
                </DocCard>
              </DocSection>
            )}

            {/* Platform Testimonials */}
            {activeSection === 'testimonials' && (
              <DocSection title="Platform Testimonials" icon={MessageSquare}>
                <DocCard title="Overview">
                  <p>Share your experience with iHostPMS! Your testimonial helps other property hosts 
                  discover the platform and make informed decisions.</p>
                </DocCard>

                <DocCard title="Submitting a Testimonial">
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Go to your <strong>Dashboard</strong></li>
                    <li>Find the "Share Your Experience" card</li>
                    <li>Click <strong>Write Testimonial</strong></li>
                    <li>Select your rating (1-5 stars)</li>
                    <li>Add your role (e.g., "Property Manager", "Airbnb Host")</li>
                    <li>Write your testimonial</li>
                    <li>Submit for review</li>
                  </ol>
                </DocCard>

                <DocCard title="Testimonial Status" highlight>
                  <div className="space-y-3 text-slate-600">
                    <div className="flex items-start gap-2">
                      <Clock className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <p><strong>Pending:</strong> Awaiting platform admin review</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <p><strong>Approved:</strong> Visible to platform visitors</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <Star className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                      <p><strong>Featured:</strong> Displayed prominently on the homepage</p>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Editing Your Testimonial">
                  <p className="text-slate-600 mb-3">If you already submitted a testimonial:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li>Click <strong>Edit Testimonial</strong> on your dashboard</li>
                    <li>Update your rating or content</li>
                    <li>Submit again for review</li>
                  </ul>
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="text-sm text-amber-700">
                      <strong>Note:</strong> Editing your testimonial resets it to "Pending" status for re-review.
                    </p>
                  </div>
                </DocCard>

                <DocCard title="Where Testimonials Appear">
                  <p className="text-slate-600">
                    Approved and featured testimonials are displayed on the iHostPMS landing page 
                    to help new hosts learn about the platform from real users like you.
                  </p>
                </DocCard>
              </DocSection>
            )}

            {/* Early Checkout */}
            {activeSection === 'early-checkout' && (
              <DocSection title="Early Checkout" icon={LogOut}>
                <DocCard title="Overview">
                  <p>Sometimes guests need to leave before their scheduled checkout date. The Early Checkout 
                  feature helps you process these situations professionally, calculate refunds, and release 
                  the room for new bookings.</p>
                </DocCard>

                <DocCard title="When to Use Early Checkout">
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li>Guest has an emergency and needs to leave early</li>
                    <li>Guest's plans changed unexpectedly</li>
                    <li>Property issue requiring guest relocation</li>
                    <li>Any situation where guest departs before scheduled checkout</li>
                  </ul>
                </DocCard>

                <DocCard title="Processing Early Checkout" highlight>
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Go to <strong>Bookings</strong> page</li>
                    <li>Find the checked-in booking</li>
                    <li>Click <strong>Early Checkout</strong> button</li>
                    <li>Select the new checkout date</li>
                    <li>Click <strong>Calculate Refund</strong> to see breakdown</li>
                    <li>Optionally add reason and notes</li>
                    <li>Select refund method (Cash, Bank Transfer, etc.)</li>
                    <li>Click <strong>Process Checkout</strong></li>
                  </ol>
                </DocCard>

                <DocCard title="Refund Calculation">
                  <p className="text-slate-600 mb-3">Refunds are calculated based on your property's cancellation policy:</p>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center p-2 bg-emerald-50 rounded-lg">
                      <span className="font-medium text-emerald-700">Flexible Policy</span>
                      <span className="text-emerald-600">100% refund for unused nights</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-amber-50 rounded-lg">
                      <span className="font-medium text-amber-700">Moderate Policy</span>
                      <span className="text-amber-600">50% refund for unused nights</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-red-50 rounded-lg">
                      <span className="font-medium text-red-700">Strict Policy</span>
                      <span className="text-red-600">No refund for early checkout</span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mt-3">
                    Formula: <code className="bg-slate-100 px-1 rounded">Price per Night × Nights Remaining × Refund %</code>
                  </p>
                </DocCard>

                <DocCard title="What Happens After Early Checkout">
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>Booking status changes to <strong>Checked Out</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>Original checkout date is preserved for records</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span><strong>Remaining dates are released</strong> for new bookings</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>Refund details are recorded with the booking</span>
                    </li>
                  </ul>
                </DocCard>

                <DocCard title="Refund Methods">
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li><strong>Cash:</strong> Refund given in cash at property</li>
                    <li><strong>Bank Transfer:</strong> Direct transfer to guest's account</li>
                    <li><strong>Original Payment:</strong> Reverse charge to original payment method</li>
                    <li><strong>Account Credit:</strong> Credit for future bookings</li>
                  </ul>
                </DocCard>
              </DocSection>
            )}

            {/* Content Moderation (Platform Admin) */}
            {activeSection === 'moderation' && (
              <DocSection title="Content Moderation" icon={Shield}>
                <DocCard title="Overview">
                  <p>Platform administrators can moderate guest reviews and testimonials across all tenants 
                  to maintain quality and remove inappropriate content.</p>
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="text-sm text-amber-700">
                      <strong>Note:</strong> This feature is only available to platform super administrators.
                    </p>
                  </div>
                </DocCard>

                <DocCard title="Accessing Content Moderation">
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Log in to the <strong>Platform Admin Portal</strong></li>
                    <li>Click <strong>Content Moderation</strong> in the sidebar</li>
                    <li>Use tabs to switch between Guest Reviews and Testimonials</li>
                  </ol>
                </DocCard>

                <DocCard title="Spam Detection" highlight>
                  <p className="text-slate-600 mb-3">The system automatically scans reviews for spam indicators:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li>URLs and email addresses</li>
                    <li>Excessive capitalization</li>
                    <li>Promotional language and spam keywords</li>
                    <li>Profanity and inappropriate content</li>
                    <li>Duplicate or repetitive content</li>
                  </ul>
                  <p className="text-sm text-slate-500 mt-3">
                    Click <strong>Scan for Spam</strong> to analyze all reviews and get spam statistics.
                  </p>
                </DocCard>

                <DocCard title="Spam Score Categories">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center p-2 bg-emerald-50 rounded-lg">
                      <span className="font-medium text-emerald-700">High Quality (0-19)</span>
                      <span className="text-emerald-600">Clean content</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-blue-50 rounded-lg">
                      <span className="font-medium text-blue-700">Good (20-39)</span>
                      <span className="text-blue-600">Minor issues</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-amber-50 rounded-lg">
                      <span className="font-medium text-amber-700">Needs Review (40-59)</span>
                      <span className="text-amber-600">Manual review recommended</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-orange-50 rounded-lg">
                      <span className="font-medium text-orange-700">Suspicious (60-79)</span>
                      <span className="text-orange-600">Likely problematic</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-red-50 rounded-lg">
                      <span className="font-medium text-red-700">Likely Spam (80-100)</span>
                      <span className="text-red-600">Auto-flagged for review</span>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Moderation Actions">
                  <div className="space-y-3 text-slate-600">
                    <div className="flex items-start gap-2">
                      <Flag className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Flag</p>
                        <p className="text-sm">Mark content for further review. Flagged items remain visible but highlighted.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Hide</p>
                        <p className="text-sm">Remove from public display but keep in database for records.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Delete</p>
                        <p className="text-sm">Permanently remove content. Use for malicious or spam content.</p>
                      </div>
                    </div>
                  </div>
                </DocCard>

                <DocCard title="Bulk Moderation">
                  <p className="text-slate-600 mb-3">For efficiency, you can moderate multiple items at once:</p>
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Check the boxes next to items you want to moderate</li>
                    <li>Use "Select All" to select all visible items</li>
                    <li>Click <strong>Flag All</strong>, <strong>Hide All</strong>, or <strong>Delete All</strong></li>
                    <li>Confirm the bulk action</li>
                  </ol>
                </DocCard>

                <DocCard title="Best Practices">
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li>Review context before taking action - genuine negative feedback is valuable</li>
                    <li>Use "Hide" for borderline content, "Delete" only for clear violations</li>
                    <li>Run spam scans periodically to catch new issues</li>
                    <li>All moderation actions are logged in the audit trail</li>
                  </ul>
                </DocCard>
              </DocSection>
            )}

            {/* Security & Passwords */}
            {activeSection === 'security' && (
              <DocSection title="Security & Passwords" icon={Lock}>
                <DocCard title="Overview">
                  <p>Keep your account secure with password management features. Change your password, 
                  reset forgotten passwords, and manage security settings.</p>
                </DocCard>

                <DocCard title="Changing Your Password">
                  <ol className="list-decimal list-inside space-y-2 text-slate-600">
                    <li>Go to <strong>Settings</strong> in the sidebar</li>
                    <li>Find the <strong>Change Password</strong> section</li>
                    <li>Enter your current password</li>
                    <li>Enter your new password (minimum 8 characters)</li>
                    <li>Confirm the new password</li>
                    <li>Click <strong>Change Password</strong></li>
                  </ol>
                </DocCard>

                <DocCard title="Forgot Password">
                  <p className="text-slate-600">If you've forgotten your password:</p>
                  <ol className="list-decimal list-inside space-y-2 text-slate-600 mt-2">
                    <li>Click <strong>Forgot password?</strong> on the login page</li>
                    <li>Enter your email address</li>
                    <li>Check your email for a password reset link</li>
                    <li>Click the link (valid for 1 hour)</li>
                    <li>Enter and confirm your new password</li>
                    <li>Log in with your new password</li>
                  </ol>
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-600">Reset links expire after 1 hour for security. If yours expired, request a new one.</p>
                  </div>
                </DocCard>

                <DocCard title="First-Time Login (Invited Users)">
                  <p className="text-slate-600">If you were invited to a team:</p>
                  <ol className="list-decimal list-inside space-y-2 text-slate-600 mt-2">
                    <li>Check your email for an invitation from iHostPMS</li>
                    <li>Use the temporary password provided in the email</li>
                    <li>You'll be prompted to set a new password immediately</li>
                    <li>Create a strong password that you'll remember</li>
                    <li>Access the dashboard after setting your password</li>
                  </ol>
                </DocCard>

                <DocCard title="Password Requirements">
                  <p className="text-slate-600">Passwords must meet the following requirements:</p>
                  <ul className="list-disc list-inside text-slate-600 mt-2 space-y-1">
                    <li>Minimum 8 characters (can be higher based on platform settings)</li>
                    <li>May require uppercase letters (A-Z)</li>
                    <li>May require lowercase letters (a-z)</li>
                    <li>May require numbers (0-9)</li>
                    <li>May require special characters (!@#$%...)</li>
                  </ul>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-sm text-blue-700">The exact requirements depend on the password policy set by your platform administrator.</p>
                  </div>
                </DocCard>

                <DocCard title="Security Best Practices">
                  <ul className="list-disc list-inside text-slate-600 space-y-2">
                    <li>Use a unique password that you don't use elsewhere</li>
                    <li>Include a mix of uppercase, lowercase, numbers, and symbols</li>
                    <li>Don't share your password with anyone</li>
                    <li>Change your password regularly</li>
                    <li>Log out when using shared computers</li>
                    <li>Enable Google Sign-In for additional security</li>
                  </ul>
                </DocCard>
              </DocSection>
            )}

            {/* Settings */}
            {activeSection === 'settings' && (
              <DocSection title="Settings" icon={Settings}>
                <DocCard title="Account Settings">
                  <p>Manage your profile, business information, and security settings.</p>
                </DocCard>

                <DocCard title="Profile">
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li>Update name, email, phone number</li>
                    <li>Change password</li>
                    <li>Upload profile photo</li>
                    <li>Set timezone and language preferences</li>
                  </ul>
                </DocCard>

                <DocCard title="Business Settings">
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li>Business name and registration</li>
                    <li>Default currency (KES, USD, EUR, etc.)</li>
                    <li>Tax settings and VAT</li>
                    <li>Invoice customization</li>
                  </ul>
                </DocCard>

                <DocCard title="Notifications">
                  <p className="text-slate-600">Configure email and push notification preferences:</p>
                  <ul className="list-disc list-inside text-slate-600 mt-2">
                    <li>New booking alerts</li>
                    <li>Payment confirmations</li>
                    <li>Guest messages</li>
                    <li>Check-in/check-out reminders</li>
                    <li>Calendar sync updates</li>
                  </ul>
                </DocCard>
              </DocSection>
            )}

            {/* Subscription */}
            {activeSection === 'subscription' && (
              <DocSection title="Subscription & Billing" icon={DollarSign}>
                <DocCard title="Subscription Plans">
                  <p>Choose the plan that fits your rental business needs.</p>
                </DocCard>

                <DocCard title="Available Plans">
                  <div className="space-y-4">
                    <PlanItem name="Starter" price="KES 1,500/mo" features={["Up to 2 properties", "Basic calendar", "iCal sync", "Email support"]} />
                    <PlanItem name="Standard" price="KES 3,500/mo" features={["Up to 10 properties", "Advanced calendar", "Channel manager", "Priority support"]} highlight />
                    <PlanItem name="Advanced" price="KES 7,500/mo" features={["Up to 50 properties", "All integrations", "API access", "Dedicated support"]} />
                    <PlanItem name="Enterprise" price="Custom" features={["Unlimited properties", "White-label options", "Custom SLA", "Account manager"]} />
                  </div>
                </DocCard>

                <DocCard title="Payment Methods">
                  <p className="text-slate-600">Subscribe using:</p>
                  <ul className="list-disc list-inside text-slate-600 mt-2">
                    <li><strong>Stripe:</strong> Credit/debit cards, recurring billing</li>
                    <li><strong>M-Pesa:</strong> Mobile money (Kenya)</li>
                  </ul>
                </DocCard>

                <DocCard title="Manage Subscription">
                  <p className="text-slate-600">
                    Access the Stripe Customer Portal to update payment methods, view invoices, 
                    change plans, or cancel subscription.
                  </p>
                </DocCard>
              </DocSection>
            )}

            {/* Platform Admin */}
            {activeSection === 'admin' && (
              <DocSection title="Platform Admin" icon={Shield}>
                <DocCard title="Admin Portal">
                  <p>Platform administrators have access to a separate admin portal for managing tenants, plans, and platform settings.</p>
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-amber-800 text-sm">
                      <strong>Access:</strong> /admin/login (separate from tenant login)
                    </p>
                  </div>
                </DocCard>

                <DocCard title="Admin Features">
                  <ul className="space-y-2 text-slate-600">
                    <li><strong>Tenant Management:</strong> View, approve, suspend, edit tenant accounts</li>
                    <li><strong>Plan Management:</strong> Edit subscription plans and features</li>
                    <li><strong>SMTP Configuration:</strong> Configure email settings with test functionality</li>
                    <li><strong>Website Content:</strong> Edit landing page, FAQs, legal documents</li>
                    <li><strong>Login As:</strong> Access tenant accounts for support (with audit trail)</li>
                    <li><strong>Audit Logs:</strong> Track all admin actions</li>
                  </ul>
                </DocCard>

                <DocCard title="Login As (Support Mode)" highlight>
                  <p className="text-slate-600 mb-3">
                    Admins can log into any tenant account for troubleshooting. When active:
                  </p>
                  <ul className="list-disc list-inside text-slate-600 space-y-1">
                    <li>Amber warning banner appears across all pages</li>
                    <li>Shows "Support Mode: Viewing [tenant name]'s account"</li>
                    <li>All actions are logged for audit compliance</li>
                    <li>"Exit Support Mode" button returns to admin dashboard</li>
                  </ul>
                </DocCard>
              </DocSection>
            )}
          </main>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-100 border-t border-slate-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-slate-500">
              <p>Need help? Contact support@ihostpms.com</p>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a href="/privacy" className="text-slate-600 hover:text-pink-600">Privacy Policy</a>
              <a href="/terms" className="text-slate-600 hover:text-pink-600">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const DocSection = ({ title, icon: Icon, children }) => (
  <div className="animate-fade-in">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
        <Icon className="w-5 h-5 text-pink-600" />
      </div>
      <h2 className="text-2xl font-bold text-slate-800">{title}</h2>
    </div>
    <div className="space-y-6">{children}</div>
  </div>
);

const DocCard = ({ title, children, highlight }) => (
  <div className={`bg-white rounded-xl shadow-sm border ${highlight ? 'border-pink-200 ring-1 ring-pink-100' : 'border-slate-200'} p-6`}>
    {title && <h3 className="font-bold text-lg text-slate-800 mb-3">{title}</h3>}
    <div className="text-slate-600">{children}</div>
  </div>
);

const FeatureItem = ({ icon: Icon, title, desc }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 bg-pink-50 rounded-lg flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-pink-500" />
    </div>
    <div>
      <h4 className="font-semibold text-slate-800 text-sm">{title}</h4>
      <p className="text-slate-500 text-xs">{desc}</p>
    </div>
  </div>
);

const StatusItem = ({ color, title, desc }) => (
  <div className="flex items-start gap-3">
    <div className={`w-3 h-3 ${color} rounded-full mt-1.5 flex-shrink-0`} />
    <div>
      <span className="font-semibold text-slate-800">{title}:</span>
      <span className="text-slate-600 ml-1">{desc}</span>
    </div>
  </div>
);

const LegendItem = ({ color, label }) => (
  <div className="flex items-center gap-2">
    <div className={`w-4 h-4 ${color} rounded`} />
    <span className="text-sm text-slate-600">{label}</span>
  </div>
);

const ChannelBadge = ({ name, color }) => (
  <div className={`${color} px-3 py-2 rounded-lg text-center text-sm font-medium`}>
    {name}
  </div>
);

const KPIItem = ({ title, desc }) => (
  <div className="border rounded-lg p-3">
    <h4 className="font-semibold text-slate-800 text-sm">{title}</h4>
    <p className="text-slate-500 text-xs">{desc}</p>
  </div>
);

const PlanItem = ({ name, price, features, highlight }) => (
  <div className={`border rounded-lg p-4 ${highlight ? 'border-pink-300 bg-pink-50/50' : ''}`}>
    <div className="flex justify-between items-center mb-2">
      <h4 className="font-bold text-slate-800">{name}</h4>
      <span className="text-pink-600 font-semibold">{price}</span>
    </div>
    <ul className="text-slate-600 text-sm space-y-1">
      {features.map((f, i) => (
        <li key={i} className="flex items-center gap-2">
          <CheckCircle className="w-3 h-3 text-emerald-500" />
          {f}
        </li>
      ))}
    </ul>
  </div>
);

export default DocsPage;
